import { createElement } from "../core/dialogBuilder.js";

import { currentTable } from "../../../../../app/viewManager.js";
import { apiFetch } from "../../../../../app/apiClient.js";
import { allActivities } from "../../../../../app/utils/enums.js";
import { hoursToTime, timeToHours } from "../../../app-table/utils/timeUtils.js";
import { ensureTrackedWorkMs } from "../../../app-time/utils/timeManager.js";

const getContent = () => document.querySelector("app-dialog")?.contentEl ?? null;

let baseData = {
    customers:    [],
    applications: [],
    projects:     [],
};
let lastTrackedWorkMs = null;

export function getCachedTrackedWorkMs() {
    return lastTrackedWorkMs;
}
const filterState = {
    Customer:    "",
    Application: "",
    Project:     "",
};
const userClearedFields = new Set();
let dropdowns = {
    customer:    null,
    application: null,
    project:     null,
};

const isAppInput = (element) => element?.tagName === 'APP-INPUT';
const notifyCascadeComplete = () => document.dispatchEvent(new CustomEvent('dialog-cascade-complete'));

const toCustomerOption = (customer) => ({ ...customer, name: customer.shortName || customer.name });

const CUSTOMERS_ENDPOINT = "customers";
const DIALOG_DATA_ENDPOINT = "entries/DialogData";
const DIALOG_INIT_ENDPOINT = "entries/DialogInit";

const getElValue = (element) => isAppInput(element) ? (element.getValue() ?? '') : (element?.value ?? '');

export function fillDropdown(elementId, list, selectedValue = null) {
    const element = getContent()?.querySelector(`#${elementId}`);
    if (!element) return;

    if (isAppInput(element)) {
        element.setOptions(list);
        if (selectedValue !== null) element.setValue(String(selectedValue), { silent: true });
        return;
    }

    element.innerHTML = "";
    element.appendChild(createElement({ type: "option", text: "", attributes: { value: "" } }));
    [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
        .forEach(item => element.appendChild(createElement({ type: "option", text: item.name, attributes: { value: item.id } })));
    if (selectedValue !== null) element.value = selectedValue;
}

function applyList(element, list, echoedId, autoSelect) {
    if (!element) return;

    if (isAppInput(element)) {
        element.setOptions(list);
        if (echoedId != null) {
            element.setValue(String(echoedId), { silent: true });
        } else if (autoSelect && list.length === 1) {
            element.setValue(String(list[0].id), { silent: true });
        } else {
            element.setValue(null, { silent: true });
        }
    } else {
        fillDropdown(element.id, list);
        if (echoedId != null) {
            element.value = echoedId;
        } else if (autoSelect && list.length === 1) {
            element.value = list[0].id;
        } else {
            element.value = "";
        }
        element.style.borderColor = "";
    }
}

let cascadeToken = 0;

async function refreshCascade(autoSelect = true, depth = 0) {
    const params = new URLSearchParams();
    if (filterState.Customer)    params.set("customerId",    filterState.Customer);
    if (filterState.Application) params.set("applicationId", filterState.Application);
    if (filterState.Project)     params.set("projectId",     filterState.Project);

    const myToken = ++cascadeToken;
    const query   = params.toString();
    const data    = await apiFetch(query ? `${DIALOG_DATA_ENDPOINT}?${query}` : DIALOG_DATA_ENDPOINT, "GET", undefined, { silentNotFound: true });

    if (cascadeToken !== myToken || !data || typeof data !== "object") return;

    const customers    = (data.customers    ?? []).map(toCustomerOption);
    const applications = data.applications ?? [];
    const projects      = data.projects     ?? [];

    applyList(dropdowns.customer,    customers,    data.customerId,    autoSelect && !userClearedFields.has("Customer"));
    applyList(dropdowns.application, applications, data.applicationId, autoSelect && !userClearedFields.has("Application"));
    applyList(dropdowns.project,     projects,     data.projectId,     autoSelect && !userClearedFields.has("Project"));

    const resolvedCustomer    = dropdowns.customer    ? getElValue(dropdowns.customer)    : '';
    const resolvedApplication = dropdowns.application ? getElValue(dropdowns.application) : '';
    const resolvedProject     = dropdowns.project     ? getElValue(dropdowns.project)     : '';

    // Wenn Auto-Select gerade Customer/Application/Project befuellt hat (silent, ohne
    // input-change auszuloesen), muessen die jeweils anderen Listen mit den neu bekannten
    // IDs nachgefiltert werden - sonst bleiben z.B. alle Projekte waehlbar, obwohl Customer
    // und Application inzwischen eindeutig feststehen.
    const changed =
        resolvedCustomer    !== filterState.Customer ||
        resolvedApplication !== filterState.Application ||
        resolvedProject     !== filterState.Project;

    filterState.Customer    = resolvedCustomer;
    filterState.Application = resolvedApplication;
    filterState.Project     = resolvedProject;

    if (changed && depth < 3) {
        await refreshCascade(autoSelect, depth + 1);
        return;
    }

    notifyCascadeComplete();
}

function getAvailableKeys(data) {
    const allowedKeys = [
        "customerId", "applicationId", "projectId",
        "activityId", "description", "payerId", "date", "workedHours"
    ];
    const filtered = (!data || data.length === 0) ? allowedKeys : Object.keys(data[0]).filter(key => allowedKeys.includes(key));

    const hasEntryFields = filtered.some(k => ["customerId", "applicationId", "projectId", "activityId"].includes(k));
    const keys = hasEntryFields ? filtered : allowedKeys;

    return keys.map(key => {
        const trimmed = key.endsWith("Id") ? key.slice(0, -2) : key;
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    });
}

export async function fillDialogDropdowns(key = null) {
    const content       = getContent();
    const availableKeys = getAvailableKeys(currentTable?.getData() ?? []);

    if (key) {
        const keyMap = {
            application: { resource: "applications", elementId: "dropdownApplication" },
            customer:    { resource: CUSTOMERS_ENDPOINT, elementId: "dropdownCustomer"    },
            project:     { resource: "projects",     elementId: "dropdownProject"     },
        };
        const entry = keyMap[key.toLowerCase()];
        if (!entry) return [];
        const raw = await apiFetch(entry.resource, "GET", undefined, { silentNotFound: true });
        const rawList = Array.isArray(raw) ? raw : [];
        const data = key.toLowerCase() === "customer" ? rawList.map(toCustomerOption) : rawList;
        fillDropdown(entry.elementId, data);
        return data;
    }

    const inputDate = content?.querySelector("#inputDate");
    if (inputDate && availableKeys.includes("Date")) {
        const today   = new Date();
        const minDate = new Date();
        minDate.setMonth(minDate.getMonth() - 1);
        if (isAppInput(inputDate)) {
            inputDate.setAttribute("max", today.toISOString().split("T")[0]);
            inputDate.setAttribute("min", minDate.toISOString().split("T")[0]);
            inputDate.setValue(today.toISOString().split("T")[0], { silent: true });
        } else {
            inputDate.setAttribute("max", today.toISOString().split("T")[0]);
            inputDate.setAttribute("min", minDate.toISOString().split("T")[0]);
            inputDate.value = today.toISOString().split("T")[0];
        }
    }

    const needsInit = ["Customer", "Application", "Project", "Payer", "Activity"].some(k => availableKeys.includes(k));
    const initData  = needsInit ? await apiFetch(DIALOG_INIT_ENDPOINT, "GET", undefined, { silentNotFound: true }) : null;

    const dropdownActivity = content?.querySelector("#dropdownActivity");
    if (dropdownActivity && availableKeys.includes("Activity")) {
        const activityIds = (initData?.activityIds ?? []).map(String);
        const availableActivities = Object.fromEntries(
            Object.entries(allActivities).filter(([id]) => activityIds.includes(id))
        );
        if (isAppInput(dropdownActivity)) {
            dropdownActivity.setOptions(Object.entries(availableActivities).map(([id, name]) => ({ id, name })));
        } else {
            dropdownActivity.innerHTML = "";
            dropdownActivity.appendChild(createElement({ type: "option", text: "", attributes: { value: "" } }));
            const sortedActivities = Object.entries(availableActivities).sort(([, a], [, b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
            for (const [id, name] of sortedActivities) {
                dropdownActivity.appendChild(createElement({ type: "option", text: name, attributes: { value: id } }));
            }
        }
    }

    baseData.customers    = (initData?.customers    ?? []).map(toCustomerOption);
    baseData.applications = initData?.applications  ?? [];
    baseData.projects     = initData?.projects      ?? [];
    lastTrackedWorkMs      = initData ? (initData.trackedWorkMs ?? 0) : null;

    if (availableKeys.includes("Payer"))        fillDropdown("dropdownBillingVia",  baseData.customers);

    filterState.Customer    = "";
    filterState.Application = "";
    filterState.Project     = "";
    userClearedFields.clear();

    if (availableKeys.includes("Customer"))    fillDropdown("dropdownCustomer",    baseData.customers);
    if (availableKeys.includes("Application")) fillDropdown("dropdownApplication", baseData.applications);
    if (availableKeys.includes("Project"))     fillDropdown("dropdownProject",     baseData.projects);

    dropdowns.customer    = availableKeys.includes("Customer")    ? content?.querySelector("#dropdownCustomer")    : null;
    dropdowns.application = availableKeys.includes("Application") ? content?.querySelector("#dropdownApplication") : null;
    dropdowns.project     = availableKeys.includes("Project")     ? content?.querySelector("#dropdownProject")     : null;

    dropdowns.customer?.addEventListener("input-change", async () => {
        const value = getElValue(dropdowns.customer);
        filterState.Customer = value;
        value ? userClearedFields.delete("Customer") : userClearedFields.add("Customer");
        await refreshCascade(true);
    });

    dropdowns.application?.addEventListener("input-change", async () => {
        const value = getElValue(dropdowns.application);
        filterState.Application = value;
        value ? userClearedFields.delete("Application") : userClearedFields.add("Application");
        await refreshCascade(true);
    });

    dropdowns.project?.addEventListener("input-change", async () => {
        const value = getElValue(dropdowns.project);
        filterState.Project = value;
        value ? userClearedFields.delete("Project") : userClearedFields.add("Project");
        await refreshCascade(true);
    });
}

export async function applyInitialFilters() {
    if (!dropdowns.customer && !dropdowns.application && !dropdowns.project) return;
    await refreshCascade(true);
}

export async function restoreCascadeFilters() {
    if (dropdowns.customer?.isConnected)    filterState.Customer    = getElValue(dropdowns.customer);
    if (dropdowns.application?.isConnected) filterState.Application = getElValue(dropdowns.application);
    if (dropdowns.project?.isConnected)     filterState.Project     = getElValue(dropdowns.project);
    await applyInitialFilters();
}

export async function selectDialogDropdowns(data) {
    const content = getContent();
    if (!content || !data) return;

    content.querySelectorAll(".dialogInput[data-field]").forEach(element => {
        const fieldName = element.dataset.field;
        if (!Object.prototype.hasOwnProperty.call(data, fieldName)) return;

        const rawValue = data[fieldName];

        if (isAppInput(element)) {
            const inputType = element.getAttribute('type');
            if (inputType === 'datepicker') {
                element.setValue(rawValue ? toISODate(rawValue) : null, { silent: true });
            } else if (inputType === 'timepicker') {
                let timeStr = null;
                if (rawValue != null && rawValue !== '') {
                    const trimmed = String(rawValue).trim();
                    if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
                        timeStr = trimmed;
                    } else {
                        const hoursValue = parseFloat(trimmed);
                        if (!isNaN(hoursValue)) timeStr = hoursToTime(hoursValue);
                    }
                }
                element.setValue(timeStr, { silent: true });
            } else if (inputType === 'text' || inputType === 'textarea') {
                element.setValue(rawValue ?? '', { silent: true });
            } else {
                const strVal = rawValue != null ? String(rawValue).trim() : null;
                element.setValue(strVal || null, { silent: true });
            }
        } else if (element.type === "checkbox") {
            element.checked = Boolean(rawValue);
        } else {
            if (element.type === "date" && rawValue) {
                element.value = toISODate(rawValue);
            } else {
                element.value = rawValue ?? "";
            }
        }
    });

    if (dropdowns.customer)    filterState.Customer    = getElValue(dropdowns.customer);
    if (dropdowns.application) filterState.Application = getElValue(dropdowns.application);
    if (dropdowns.project)     filterState.Project     = getElValue(dropdowns.project);

    await applyInitialFilters();
}

function toISODate(value) {
    const str = String(value);
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(str)) {
        const [day, month, year] = str.split(".");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    const parsedDate = new Date(str);
    return isNaN(parsedDate.getTime()) ? "" : parsedDate.toISOString().split("T")[0];
}

export async function setHoursWarnTime() {
    const content = getContent();
    if (!content) return;
    const input = content.querySelector('#inputHoursWorked');
    if (!input) return;

    const today = new Date().toISOString().split('T')[0];
    const dateInput = content.querySelector('#inputDate');
    const selectedDate = isAppInput(dateInput) ? dateInput.getValue() : (dateInput?.value ?? '');
    if (selectedDate && selectedDate !== today) return;

    const trackedMs = await ensureTrackedWorkMs(lastTrackedWorkMs);
    if (!trackedMs || trackedMs <= 0) return;

    const trackedHours = trackedMs / (1000 * 60 * 60);

    const entries = currentTable?.tableName === 'entry' ? currentTable.getData() : [];
    const bookedHours = entries
        .filter(event => event.date === today)
        .reduce((sum, event) => sum + timeToHours(event.workedHours), 0);

    const available = Math.max(0, Math.min(trackedHours - bookedHours, 10));
    input.setAttribute('warn-time', hoursToTime(available));
}